import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminSession, ADMIN_ROLE_LABEL } from './adminSession';
import { AdminOrderAlertPopup, useAdminOrderNotifications } from './AdminOrderAlert';
import './AdminLayout.scss';

// 사이드바 메뉴 아이콘 (선 아이콘, 색은 글자색을 따름)
const NAV_ICONS = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>
  ),
  inquiries: (
    <>
      <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-8l-5 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
      <path d="M8 9h8M8 13h5" />
    </>
  ),
  orders: (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1" />
      <path d="m8.5 11 1.5 1.5 3-3" />
      <path d="M8.5 16.5h7" />
    </>
  ),
  products: (
    <>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8z" />
      <path d="m3 8 9 5 9-5" />
      <path d="M12 13v8" />
    </>
  ),
  portfolio: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-5-5-9 9" />
    </>
  ),
  history: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  certifications: (
    <>
      <circle cx="12" cy="9" r="6" />
      <path d="m9 14.2-1.5 6.8 4.5-2.5 4.5 2.5-1.5-6.8" />
    </>
  ),
  partners: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <circle cx="17" cy="9.5" r="2.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M15.5 14.5A4.5 4.5 0 0 1 21.5 19" />
    </>
  ),
  chatbot: (
    <>
      <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-8l-5 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" />
      <path d="M8 11h.01M12 11h.01M16 11h.01" />
    </>
  ),
  accounts: (
    <>
      <circle cx="10" cy="8" r="4" />
      <path d="M3 21a7 7 0 0 1 10.5-6" />
      <path d="M18 13.5 15 15v2.2c0 1.8 1.3 3.2 3 3.8 1.7-.6 3-2 3-3.8V15l-3-1.5z" />
    </>
  ),
};

interface NavItem {
  label: string;
  icon: keyof typeof NAV_ICONS;
  to?: string;
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

// G-WORKS 심볼마크: 로고 색(짙은 남보라→보라) 배경 위에 세리프 G
function BrandSymbol({ size = 38 }: { size?: number }) {
  return (
    <svg className="brand-symbol" width={size} height={size} viewBox="0 0 40 40" role="img" aria-label="G-WORKS">
      <defs>
        <linearGradient id="gwSymbolBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#24133A" />
          <stop offset="100%" stopColor="#5B3A9B" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="11" fill="url(#gwSymbolBg)" />
      {/* 가운데 세리프 G */}
      <text
        x="20"
        y="27.5"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="21"
        fontWeight="700"
        fill="#fff"
      >
        G
      </text>
    </svg>
  );
}

function formatToday(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const weekday = new Intl.DateTimeFormat('ko-KR', { weekday: 'short' }).format(now);
  return `${y}-${m}-${d} (${weekday})`;
}

interface AdminLayoutProps {
  title: string;
  children: ReactNode;
}

function AdminLayout({ title, children }: AdminLayoutProps) {
  const [session, setSession] = useAdminSession();
  const location = useLocation();
  const navigate = useNavigate();
  // 30초마다 새로 들어온 주문/견적문의를 확인해 팝업으로 알린다
  const { pending, alerts, dismissAlert } = useAdminOrderNotifications(session?.token);

  if (!session) {
    return (
      <div className="gwAdmin gwAdminAuthGate">
        <div className="authCard">
          <div className="brand-mark" style={{ width: 48, height: 48, margin: '0 auto 16px' }}>
            <BrandSymbol size={48} />
          </div>
          <h1>관리자 로그인이 필요합니다</h1>
          <p>계속하려면 관리자 계정으로 로그인해주세요.</p>
          <Link to="/admin/login" className="btn btn-primary">
            로그인 하러가기
          </Link>
        </div>
      </div>
    );
  }

  const navGroups: NavGroup[] = [
    {
      label: '메인',
      items: [
        { label: '대시보드', icon: 'dashboard', to: '/admin' },
        { label: '견적문의', icon: 'inquiries', to: '/admin/inquiries', badge: pending?.inquiries },
        { label: '주문관리', icon: 'orders', to: '/admin/orders', badge: pending?.orders },
        { label: '주문상품 관리', icon: 'products', to: '/admin/portfolio' },
        { label: '포트폴리오 관리', icon: 'portfolio', to: '/admin/works' },
        { label: '연혁 관리', icon: 'history', to: '/admin/company-history' },
        { label: '인증 관리', icon: 'certifications', to: '/admin/certifications' },
        { label: '협력사 관리', icon: 'partners', to: '/admin/partners' },
        { label: '챗봇 관리', icon: 'chatbot', to: '/admin/chatbot' },
      ],
    },
  ];

  if (session.admin.role === 'SUPER_ADMIN') {
    navGroups.push({
      label: '관리',
      items: [{ label: '관리자 계정', icon: 'accounts', to: '/admin/accounts' }],
    });
  }

  const isActive = (to: string) => (to === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(to));

  const handleLogout = () => {
    setSession(null);
    navigate('/admin/login');
  };

  return (
    <div className="gwAdmin">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <BrandSymbol />
          </div>
          <div className="brand-text">
            <strong>G-WORKS</strong>
            <span>관리자 모드</span>
          </div>
        </div>

        {navGroups.map((group) => (
          <div className="menu-group" key={group.label}>
            <div className="menu-label">{group.label}</div>
            <div className="menu">
              {group.items.map((item) =>
                item.to ? (
                  <Link key={item.label} to={item.to} className={`menu-item ${isActive(item.to) ? 'active' : ''}`}>
                    <span className="ic">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        {NAV_ICONS[item.icon]}
                      </svg>
                    </span> {item.label}
                    {!!item.badge && <span className="badge">{item.badge}</span>}
                  </Link>
                ) : (
                  <div key={item.label} className="menu-item disabled" title="준비 중인 기능입니다">
                    <span className="ic">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        {NAV_ICONS[item.icon]}
                      </svg>
                    </span> {item.label}
                  </div>
                ),
              )}
            </div>
          </div>
        ))}

        <div className="sidebar-foot">
          {/* 프로필 아바타: 연한 파랑 원 + 파란 선 사람 아이콘 (대시보드 카드 아이콘과 같은 스타일) */}
          <span className="foot-avatar" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21a8 8 0 0 1 16 0" />
            </svg>
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
            <strong style={{ fontSize: 13 }}>{session.admin.name || session.admin.username}</strong>
            <span style={{ fontSize: 11, color: 'var(--ink-500)' }}>{ADMIN_ROLE_LABEL[session.admin.role]}</span>
          </div>
          <button type="button" className="logout" onClick={handleLogout}>
            로그아웃
          </button>
        </div>
      </aside>

      <main>
        <div className="topbar">
          {/* 왼쪽: 큰 제목 + 아래 작은 부제(메인 · 날짜) */}
          <div className="crumbs">
            <strong className="crumbs-title">{title}</strong>
            <div className="crumbs-sub">
              <span>메인</span>
              <span className="sep">/</span>
              <span>{title}</span>
              <span className="sep">·</span>
              <span>{formatToday()}</span>
            </div>
          </div>
          <div className="topbar-right">
            <div className="search-pill">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input placeholder="고객사, 견적번호, 박스 타입 검색…" />
            </div>
            <div className="icon-btn">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
              {(!!pending?.inquiries || !!pending?.orders || alerts.length > 0) && <span className="dot"></span>}
            </div>
            <Link to="/" className="btn btn-outline">
              홈
            </Link>
            <Link to="/admin/inquiries" className="btn btn-secondary">
              견적 회신
            </Link>
          </div>
        </div>

        {children}
      </main>

      {/* 신규 주문 알림 팝업 */}
<AdminOrderAlertPopup alerts={alerts} onDismiss={dismissAlert} />
    </div>
  );
}

export default AdminLayout;
